import React from "react";
import styled from "styled-components";
import Card from "./Card";
import PremiumHeaderButton from "./PremiumHeaderButton";
import PremiumLoadingCard from "./PremiumLoadingCard";
import { premiumTheme as theme } from "./premiumTheme";
import PremiumStatusChip from "../lists/PremiumStatusChip";
import CheckboxVector from "../checkbox/CheckboxVector";

// #region SCOPED CSS
const Summary = styled(Card)`
    && { position: relative; isolation: isolate; width: 100%; max-width: 100%; min-width: 0; font-family: ${theme.fontFamily}; font-weight: 400;
        box-shadow: 0 16px 40px rgba(0,0,0,.35); }
    && > header { padding: 0 10px; column-gap: 10px; align-items: center; }
    && > header > .premium-card-header-title { height: 22px; line-height: 22px; align-self: center;
        color: ${theme.panelEmpty}; font-weight: 400; }
    && > header > .premium-card-header-right { height: 22px; align-self: center; align-items: center; }
    &.is-loading > header > .premium-card-header-title { opacity: .6; }
    .summary-content { padding: 10px 10px 0; }
    .summary-media { position: relative; height: 56px; background: ${theme.fieldBackground};
        border: 1px solid rgba(255,255,255,.06); border-radius: 8px;
        display: flex; align-items: center; justify-content: center; overflow: hidden; }
    .summary-media > img { max-width: 76%; max-height: 66%; object-fit: contain; }
    .summary-loading-overlay { position: absolute; inset: 0; z-index: 2;
        display: flex; align-items: center; justify-content: center; padding: 10px;
        background: rgba(0,0,0,.65); border-radius: inherit; }
    .summary-loading-overlay > [role="status"] { width: 100%; max-width: 240px;
        min-width: 0; border: 0; box-shadow: none; background: transparent;
        border-radius: 0; padding: 12px 10px; }
    .summary-loading-overlay .premium-loading-title { margin-bottom: 8px; }
    .summary-loading-overlay .premium-loading-slider { height: 24px; }
    .summary-selection { position: absolute; top: 10px; left: 10px; padding: 4px;
        border: 0; background: transparent; cursor: pointer; line-height: 0; z-index: 1; }
    .summary-selection:disabled { cursor: not-allowed; }
    .summary-selection:focus-visible { outline: 2px solid ${theme.mandatory}; outline-offset: 2px; }
    .summary-identity { min-height: 40px; margin-top: 8px; }
    .summary-heading { margin: 0; color: #d1d1d1; font-size: 16px; line-height: 22px;
        font-weight: 400; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .summary-id { margin: 2px 0 0; color: ${theme.panelMuted}; font-size: 11px; line-height: 16px;
        font-weight: 400; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .summary-details-divider { display: flex; align-items: center; gap: 10px;
        margin: 6px 0 4px; color: ${theme.panelEmpty}; font-size: 10px;
        line-height: 16px; font-weight: 400; letter-spacing: 1px; }
    .summary-details-divider::before, .summary-details-divider::after { content: "";
        height: 1px; background: ${theme.panelSeparator}; flex: 1; }
    .summary-status-content { display: flex; align-items: center; justify-content: flex-end;
        height: 22px; line-height: 22px; gap: 4px; min-width: 0; max-width: 100%; }
    .summary-status { display: block; flex: 0 1 auto; min-width: 0; max-width: 100%;
        overflow: hidden; text-overflow: ellipsis; font-weight: 400; }
    .summary-status-content > :not(.summary-status) { flex: 0 0 auto; }
    .summary-field dd.summary-status-value { overflow: visible; }
    .summary-fields { display: grid; gap: 0; margin: 0; }
    .summary-field { display: grid; grid-template-columns: 100px minmax(0,1fr); gap: 8px;
        align-items: start; min-height: 22px; padding: 2px; box-sizing: border-box; }
    .summary-field dt { color: ${theme.panelMuted}; font-size: 13px; line-height: 18px;
        font-weight: 400; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .summary-field dd { color: ${theme.valueColor}; text-align: right; margin: 0;
        font-size: 13px; line-height: 18px; font-weight: 400; min-width: 0;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-variant-numeric: tabular-nums; }
    .summary-field dd.is-accent { color: ${theme.mandatory}; }
    .summary-metrics { margin: 8px 0 0; padding: 8px 0; display: grid;
        grid-template-columns: minmax(0,.9fr) minmax(0,1.1fr); gap: 10px;
        border-top: 1px solid ${theme.panelSeparator}; }
    .summary-metric { display: grid; grid-template-columns: auto minmax(0,1fr);
        align-items: center; gap: 8px; min-width: 0; }
    .summary-metric dt { min-height: 16px; font-size: 12px; line-height: 16px; color: ${theme.panelMuted};
        font-weight: 400; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .summary-metric dd { min-height: 22px; margin: 0; font-size: 13px; line-height: 22px;
        text-align: right;
        font-weight: 400; color: ${theme.valueColor}; min-width: 0; white-space: nowrap;
        overflow: hidden; text-overflow: ellipsis; font-variant-numeric: tabular-nums; }
    .summary-metric dd.is-accent { color: ${theme.mandatory}; }
    .summary-metric + .summary-metric { padding-left: 10px; border-left: 1px solid ${theme.panelSeparator}; }
    &.is-orders-v14 .summary-status-field { margin-top: 5px; align-items: center; }
    &.is-orders-v14 .summary-status-field > dt,
    &.is-orders-v14 .summary-status-field > dd { line-height: 22px; }
    &.is-orders-v14 .summary-status-field > dd { display: flex; align-items: center; justify-content: flex-end; }
    &.is-orders-v14 .summary-metrics { position: relative; grid-template-columns: repeat(2, minmax(0,1fr)); column-gap: 0; }
    &.is-orders-v14 .summary-metrics::after { content: ""; position: absolute; top: 8px; bottom: 8px;
        left: 50%; width: 1px; transform: translateX(-50%); background: ${theme.panelSeparator}; pointer-events: none; }
    &.is-orders-v14 .summary-metric:first-child { padding-right: 10px; }
    &.is-orders-v14 .summary-metric + .summary-metric { padding-left: 10px; border-left: 0; }
    &.is-orders-v14 .summary-metric dt, &.is-orders-v14 .summary-metric dd {
        font-size: 13px; font-weight: 400; line-height: 22px; min-height: 22px; }
    .summary-update-line { display: flex; align-items: center; justify-content: center; gap: 8px;
        margin: 0; padding: 8px 0; min-width: 0; border-top: 1px solid ${theme.panelSeparator}; }
    .summary-update-line dt, .summary-update-line dd { margin: 0; min-width: 0;
        font-size: 13px; font-weight: 400; line-height: 22px; white-space: nowrap; }
    .summary-update-line dt { color: ${theme.panelMuted}; overflow: hidden; text-overflow: ellipsis; }
    .summary-update-line dd { color: ${theme.valueColor}; font-variant-numeric: tabular-nums; flex: 0 0 auto; }
    .summary-update-line dd.is-accent { color: ${theme.mandatory}; }
    .summary-update-line .summary-skeleton { width: 84px; }
    .summary-update-line .summary-skeleton.is-value { width: 24px; margin: 0; }
    .summary-controls { min-width: 0; padding: 10px; border-top: 1px solid ${theme.panelSeparator}; }
    .summary-actions { display: flex; flex-direction: column; gap: 8px;
        padding: 20px 10px; border-top: 1px solid ${theme.panelSeparator}; }
    .summary-action-row { display: grid;
        grid-template-columns: repeat(var(--summary-action-columns), minmax(0,1fr)); gap: 8px; }
    .summary-action-row > button { width: 100%; min-width: 0; font-weight: 400; }
    .summary-action-row > .is-emphasized:enabled { color: ${theme.mandatory};
        border-color: rgba(239,191,115,.35); }
    .summary-skeleton { display: block; height: 12px; width: 65%; background: #303437; border-radius: 4px; }
    .summary-skeleton.is-title { width: 76%; height: 18px; margin: 2px 0; }
    .summary-skeleton.is-id { width: 52%; height: 10px; margin-top: 6px; }
    .summary-skeleton.is-status { height: 22px; width: 118px; border-radius: 6px; }
    .summary-field dt .summary-skeleton { margin: 3px 0; }
    .summary-skeleton.is-value { margin: 3px 0 3px auto; width: 90%; }
    .summary-skeleton.is-metric { height: 18px; width: 44%; }
    .summary-empty-media { color: ${theme.panelMuted}; font-size: 26px; }
    &.is-empty .summary-heading, &.is-empty .summary-id,
    &.is-empty dt, &.is-empty dd, &.is-empty .summary-status {
        color: var(--text-inactive, #4f5b6d); }
    @media (max-width: 600px) {
        .summary-content { padding: 10px 10px 0; }
        .summary-actions { padding: 20px 10px; }
        .summary-field { grid-template-columns: 100px minmax(0,1fr); gap: 8px; }
    }
`;
// #endregion

/**
 * Premium Library reusable compact summary card, with opt-in Orders v14 layout.
 * Full sizing, typography, state and reuse contract: ./PremiumSummaryCard.md.
 * Presentation only. No requests, routes, raw record mapping, or permissions.
 * state: "ready" | "empty" | "loading". Empty is the inactive designer placeholder.
 * fields/metrics: [{ key, label, value }], formatted by the owning page.
 * fields and metrics may set valueTone: "accent" for a ready-state value in amber.
 * actions: [{ key, label, row, onClick, disabled, busy, emphasis }]. Rows retain caller order.
 * status: { value, label, colorClass, adornment }, with caller-owned semantic mapping.
 * statusPlacement: "header" (default) | "details" (after fields); statusLabel defaults to "Status".
 * locked disables selection/actions and interactive adornments without changing geometry.
 * ordersLayout defaults false; updateRow optionally supplies a full-width label/value after totals.
 * Plain-text identity/field/metric values keep full native hover titles when visually truncated.
 * Reuses Card (and CardHeader), PremiumHeaderButton, PremiumStatusChip,
 * CheckboxVector and the compact PremiumLoadingCard. Existing components are unchanged.
 */
export default function PremiumSummaryCard({ title, media, heading, identifier,
    fields = [], metrics = [], actions = [], controls, headerRightContent, detailsLabel = "DETAILS", status, statusPlacement = "header",
    statusLabel = "Status", ordersLayout = false, updateRow, state = "ready", selectable = false, locked = false,
    selected = false, onSelectedChange, selectionLabel = "Select summary", className = "" }) {
    const isLoading = state === "loading";
    const isEmpty = state === "empty";
    const inactive = isLoading || isEmpty;
    const actionRows = Array.from(new Set(actions.map(action => action.row ?? 0)))
        .map(row => ({ row, actions: actions.filter(action => (action.row ?? 0) === row) }));
    const withColon = label => `${String(label ?? "").replace(/:\s*$/, "")}:`;
    const fullText = value => typeof value === "string" || typeof value === "number" ? String(value) : undefined;
    const statusNode = isLoading
        ? <span className="summary-skeleton is-status" aria-hidden="true" />
        : status && <span className="summary-status-content">
            <PremiumStatusChip className="summary-status" statusValue={isEmpty ? "--" : status.value}
                label={isEmpty ? "--" : status.label} colorClass={isEmpty ? undefined : status.colorClass} />
            {!isEmpty && !locked && status.adornment}
        </span>;

    return <Summary title={title} headingLevel="h3" bodyPadding={false}
        rightContent={statusPlacement === "header" ? statusNode : headerRightContent}
        className={`premium-summary-card ${ordersLayout ? "is-orders-v14" : ""} ${isEmpty ? "is-empty" : ""} ${isLoading ? "is-loading" : ""} ${className}`}>
        <div className="summary-content" aria-busy={isLoading || undefined}>
            <div className="summary-media">
                {selectable && <button type="button" className="summary-selection" role="checkbox"
                    aria-checked={inactive ? false : selected} aria-label={selectionLabel}
                    disabled={inactive || locked || !onSelectedChange}
                    onClick={() => { if (!inactive && !locked) onSelectedChange?.(!selected); }}>
                    <span aria-hidden="true"><CheckboxVector isChecked={!inactive && selected}
                        disabled={inactive || locked || !onSelectedChange} checkboxColor={theme.mandatory} /></span>
                </button>}
                {isLoading ? null
                    : isEmpty ? <span className="summary-empty-media" aria-hidden="true">--</span> : media}
            </div>
            <div className="summary-identity">
                {isLoading ? <><span className="summary-skeleton is-title" /><span className="summary-skeleton is-id" /></>
                    : <><p className="summary-heading" title={!inactive ? fullText(heading) : undefined}>{isEmpty ? "--" : heading ?? "--"}</p>
                        <p className="summary-id" title={!inactive ? `BOtag ID: ${identifier ?? "--"}` : undefined}>BOtag ID: {isEmpty ? "--" : identifier ?? "--"}</p></>}
            </div>
            <div className="summary-details-divider">{detailsLabel}</div>
            <dl className="summary-fields" aria-hidden={isLoading || undefined}>
                {fields.map(field => <div className="summary-field" key={field.key}>
                    <dt title={!inactive ? withColon(field.label) : undefined}>{isLoading ? <span className="summary-skeleton" /> : withColon(field.label)}</dt>
                    <dd title={!inactive ? fullText(field.value) : undefined}
                        className={!inactive && field.valueTone === "accent" ? "is-accent" : undefined}>
                        {isLoading ? <span className="summary-skeleton is-value" /> : isEmpty ? "--" : field.value ?? "--"}</dd>
                </div>)}
                {statusPlacement === "details" && (status || isLoading) && <div className="summary-field summary-status-field">
                    <dt>{isLoading ? <span className="summary-skeleton" /> : withColon(statusLabel)}</dt>
                    <dd className="summary-status-value">{statusNode}</dd>
                </div>}
            </dl>
            <dl className="summary-metrics" aria-hidden={isLoading || undefined}>
                {metrics.map(metric => <div className="summary-metric" key={metric.key}>
                    <dt title={!inactive ? withColon(metric.label) : undefined}>{isLoading ? <span className="summary-skeleton" /> : withColon(metric.label)}</dt>
                    <dd title={!inactive ? fullText(metric.value) : undefined}
                        className={!inactive && metric.valueTone === "accent" ? "is-accent" : undefined}>
                        {isLoading ? <span className="summary-skeleton is-metric" /> : isEmpty ? "--" : metric.value ?? "--"}</dd>
                </div>)}
            </dl>
            {updateRow && <dl className="summary-update-line" aria-hidden={isLoading || undefined}>
                <dt title={!inactive ? withColon(updateRow.label) : undefined}>
                    {isLoading ? <span className="summary-skeleton" /> : withColon(updateRow.label)}</dt>
                <dd title={!inactive ? fullText(updateRow.value) : undefined}
                    className={!inactive && updateRow.valueTone === "accent" ? "is-accent" : undefined}>
                    {isLoading ? <span className="summary-skeleton is-value" /> : isEmpty ? "--" : updateRow.value ?? "-"}</dd>
            </dl>}
        </div>
        {controls && <div className="summary-controls">{controls}</div>}
        <div className="summary-actions" role="group" aria-label={`${title} actions`}>
            {actionRows.map(({ row, actions: rowActions }) => <div key={row} className="summary-action-row"
                style={{ "--summary-action-columns": rowActions.length }}>
            {rowActions.map(action => <PremiumHeaderButton key={action.key} label={action.label}
                className={action.emphasis ? "is-emphasized" : undefined} busy={!inactive && action.busy}
                disabled={inactive || locked || action.disabled || !action.onClick}
                onClick={event => { if (!inactive && !locked && !action.disabled && !action.busy) action.onClick?.(event); }} />)}
            </div>)}
        </div>
        {isLoading && <div className="summary-loading-overlay">
            <PremiumLoadingCard compact title="Loading summary…" showHeader={false} showFooter={false} />
        </div>}
    </Summary>;
}

export function PremiumSummaryCardSkeleton(props) {
    return <PremiumSummaryCard {...props} state="loading" />;
}
